import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, Observable } from 'rxjs';
import { NgClass } from '@angular/common';
import { ModalComponent } from '../../../../shared/components/modal/modal';
import { SelectComponent, SelectOption } from '../../../../shared/components/select/select';
import { TaxonomyMemberService } from '../../../../core/services/taxonomy-member.service';
import { ProgramService } from '../../../../core/services/program.service';
import { ReportRecipientService } from '../../../../core/services/report-recipient.service';
import { DataElementService } from '../../../../core/services/data-element.service';
import { ToastService } from '../../../../core/services/toast.service';
import {
  SituationTaxonomyMember,
  ResponseTaxonomyMember,
  ReportingRequirement,
  CdeSituationTaxonomyMember,
} from '../../../../shared/models/taxonomy-member.models';
import { ReportRecipient } from '../../../../shared/models/report-recipient.models';
import { apiErrorMessage } from '../../../../shared/utils/api-error';

type TaxonomyMember = SituationTaxonomyMember | ResponseTaxonomyMember;

@Component({
  selector: 'app-edit-taxonomy-member-modal',
  imports: [ModalComponent, NgClass, SelectComponent],
  templateUrl: './edit-taxonomy-member-modal.html',
})
export class EditTaxonomyMemberModalComponent implements OnInit {
  member = input.required<TaxonomyMember>();
  type = input.required<'situation' | 'response'>();
  closed = output<void>();
  saved = output<void>();
  removed = output<void>();

  private taxonomyMemberService = inject(TaxonomyMemberService);
  private programService = inject(ProgramService);
  private reportRecipientService = inject(ReportRecipientService);
  private dataElementService = inject(DataElementService);
  private toastService = inject(ToastService);
  private destroyRef = inject(DestroyRef);

  private initialValues = signal<{
    definition: string;
    active: boolean;
    displayOrder: string;
    programId: string;
    recipientIds: string;
    dataElementIds: string;
  } | null>(null);

  protected programOptions = signal<SelectOption[]>([]);
  protected programId = signal('');
  protected definition = signal('');
  protected active = signal(true);
  /** Display order as typed; empty = no order (sorted by name after the ordered ones). */
  protected displayOrder = signal('');
  protected displayOrderInvalid = computed(() => {
    const v = this.displayOrder().trim();
    return v !== '' && !Number.isFinite(Number(v));
  });
  protected submitting = signal(false);
  protected removing = signal(false);
  protected loading = signal(false);
  protected hasChanges = computed(() => {
    const iv = this.initialValues();
    if (!iv) return false;
    const currentIds = [...this.selectedRecipients().map((r) => r.id)].sort().join(',');
    return (
      this.definition().trim() !== iv.definition ||
      this.active() !== iv.active ||
      this.displayOrder().trim() !== iv.displayOrder ||
      this.programId() !== iv.programId ||
      currentIds !== iv.recipientIds ||
      dataElementKey(this.linkedDataElements()) !== iv.dataElementIds
    );
  });

  protected allRecipients = signal<ReportRecipient[]>([]);
  protected selectedRecipients = signal<ReportRecipient[]>([]);
  protected showRecipientSelect = signal(false);

  protected availableRecipients = computed<SelectOption[]>(() => {
    const selectedIds = new Set(this.selectedRecipients().map((r) => r.id));
    return this.allRecipients()
      .filter((r) => !selectedIds.has(r.id))
      .map((r) => ({ value: r.id, label: r.name }));
  });

  // Data elements linked to the category (JCCA-159), situation taxonomies only.
  private allDataElements = signal<{ id: string; name: string }[]>([]);
  protected linkedDataElements = signal<CdeSituationTaxonomyMember[]>([]);
  protected showDataElementSelect = signal(false);

  protected availableDataElements = computed<SelectOption[]>(() => {
    const linked = new Set(this.linkedDataElements().map((l) => l.configuredDataElementId));
    return this.allDataElements()
      .filter((d) => !linked.has(d.id))
      .map((d) => ({ value: d.id, label: d.name }));
  });

  protected get categoryName(): string {
    const m = this.member();
    if ('situationCategoryName' in m) return m.situationCategoryName;
    return m.responseCategory.name;
  }

  ngOnInit(): void {
    this.reportRecipientService
      .getReportRecipients()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (res) => this.allRecipients.set(res.reportRecipientss ?? []) });

    if (this.type() === 'situation') {
      this.loading.set(true);
      this.dataElementService
        .getDataElements()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) =>
            this.allDataElements.set(
              (res.configuredDataElements ?? []).map((d) => ({ id: d.id, name: d.name })),
            ),
        });

      this.programService
        .getPrograms({ pageSize: 100, pageIndex: 0, filter: '' })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) =>
            this.programOptions.set(res.programs.map((p) => ({ value: p.id, label: p.name }))),
        });

      forkJoin({
        detail: this.taxonomyMemberService.getSituationTaxonomyMemberById(this.member().id),
        links: this.taxonomyMemberService.getCdeSituationTaxonomyMembers(this.member().id),
      })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: ({ detail, links: linkRes }) => {
            const definition = detail.definition ?? '';
            const active = detail.active;
            const programId = detail.programId ?? '';
            const recipients = detail.reportingRequirements?.map((r) => r.reportRecipient) ?? [];
            const links = [...(linkRes.cdeSituationTaxonomyMembers ?? [])].sort(
              (a, b) => a.displayOrder - b.displayOrder,
            );
            const displayOrder = orderText(detail.displayOrder);
            this.definition.set(definition);
            this.active.set(active);
            this.displayOrder.set(displayOrder);
            this.programId.set(programId);
            this.selectedRecipients.set(recipients);
            this.linkedDataElements.set(links);
            this.initialValues.set({
              definition,
              active,
              displayOrder,
              programId,
              recipientIds: recipients
                .map((r) => r.id)
                .sort()
                .join(','),
              dataElementIds: dataElementKey(links),
            });
            this.loading.set(false);
          },
          error: () => this.loading.set(false),
        });
    } else {
      const m = this.member() as ResponseTaxonomyMember;
      const definition = m.definition ?? '';
      const active = m.active;
      const recipients = m.reportRecipients ?? [];
      const displayOrder = orderText(m.displayOrder);
      this.definition.set(definition);
      this.active.set(active);
      this.displayOrder.set(displayOrder);
      this.selectedRecipients.set(recipients);
      this.initialValues.set({
        definition,
        active,
        displayOrder,
        programId: '',
        dataElementIds: '',
        recipientIds: recipients
          .map((r) => r.id)
          .sort()
          .join(','),
      });
    }
  }

  protected addRecipient(id: string): void {
    if (!id) return;
    const recipient = this.allRecipients().find((r) => r.id === id);
    if (!recipient) return;
    this.selectedRecipients.update((list) => [...list, recipient]);
    this.showRecipientSelect.set(false);
  }

  protected cancelAddRecipient(): void {
    this.showRecipientSelect.set(false);
  }

  protected removeRecipient(id: string): void {
    this.selectedRecipients.update((list) => list.filter((r) => r.id !== id));
  }

  protected addDataElement(id: string): void {
    const element = this.allDataElements().find((d) => d.id === id);
    if (!element) return;
    this.linkedDataElements.update((list) => [
      ...list,
      {
        configuredDataElementId: element.id,
        configuredDataElementName: element.name,
        // The link requires a begin date; it applies from today.
        beginDate: todayIso(),
        endDate: null,
        displayOrder: list.length + 1,
      },
    ]);
    this.showDataElementSelect.set(false);
  }

  protected setDataElementRequired(configuredDataElementId: string, required: boolean): void {
    this.linkedDataElements.update((list) =>
      list.map((l) =>
        l.configuredDataElementId === configuredDataElementId
          ? { ...l, requiredOptional: required ? 1 : 0 }
          : l,
      ),
    );
  }

  protected removeDataElement(configuredDataElementId: string): void {
    this.linkedDataElements.update((list) =>
      list.filter((l) => l.configuredDataElementId !== configuredDataElementId),
    );
  }

  protected dataElementName(link: CdeSituationTaxonomyMember): string {
    return (
      link.configuredDataElementName ??
      this.allDataElements().find((d) => d.id === link.configuredDataElementId)?.name ??
      'Data element'
    );
  }

  protected onSubmit(): void {
    if (this.displayOrderInvalid()) return;
    this.submitting.set(true);
    const order = this.displayOrder().trim();
    const displayOrder = order === '' ? null : Number(order);
    const m = this.member();
    const sm = m as SituationTaxonomyMember;
    const existingReqs = sm.reportingRequirements ?? [];
    const reportingRequirements: ReportingRequirement[] = this.selectedRecipients().map((r) => {
      const existing = existingReqs.find((req) => req.reportRecipient.id === r.id);
      return existing ?? { reportRecipient: r };
    });

    const request$: Observable<unknown> =
      this.type() === 'situation'
        ? this.taxonomyMemberService.updateSituationTaxonomyMember({
            ...(m as SituationTaxonomyMember),
            definition: this.definition().trim(),
            active: this.active(),
            displayOrder,
            programId: this.programId() || undefined,
            reportingRequirements,
            // Always sent: the API deletes any link that is not in the payload.
            cdeSituationTaxonomyMembers: this.linkedDataElements().map((l, i) => ({
              ...l,
              displayOrder: i + 1,
            })),
          })
        : this.taxonomyMemberService.updateResponseTaxonomyMember({
            ...(m as ResponseTaxonomyMember),
            definition: this.definition().trim(),
            active: this.active(),
            displayOrder,
            reportRecipients: this.selectedRecipients(),
          });

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toastService.show('Category updated successfully');
        this.saved.emit();
      },
      error: (err) => {
        this.toastService.show(apiErrorMessage(err, 'Failed to update category'), 'error');
        this.submitting.set(false);
      },
    });
  }

  protected onRemove(): void {
    this.removing.set(true);
    const id = this.member().id;
    const request$: Observable<unknown> =
      this.type() === 'situation'
        ? this.taxonomyMemberService.deleteSituationTaxonomyMember(id)
        : this.taxonomyMemberService.deleteResponseTaxonomyMember(id);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toastService.show('Category removed from taxonomy');
        this.removed.emit();
      },
      error: (err) => {
        this.toastService.show(apiErrorMessage(err, 'Failed to remove category'), 'error');
        this.removing.set(false);
      },
    });
  }
}

/** Order-sensitive key of the linked data elements, for change detection. */
function dataElementKey(links: CdeSituationTaxonomyMember[]): string {
  return links.map((l) => `${l.configuredDataElementId}:${l.requiredOptional === 1}`).join(',');
}

function todayIso(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}T00:00:00`;
}

function orderText(order: number | null | undefined): string {
  return order === null || order === undefined ? '' : String(order);
}
